import { TickCircle } from '@/components/icons';
import { Badge, badgeVariants } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { formatRelativeToNow } from '@/lib/date';
import { cn } from '@/lib/utils';
import { derivePatientStatus, type PatientStatusTab } from './patient-status';
import type { PatientAnamnesis } from '@/types/patient-anamnesis';
import type {
    PatientReportOverview,
    PatientSurveyOverview,
    PatientTestOverview,
} from '@/types/patient-overview';

type Props = {
    surveys: PatientSurveyOverview[];
    tests: PatientTestOverview[];
    reports: PatientReportOverview[];
    anamnesis?: PatientAnamnesis | null;
    activeImports?: number;
    onSelectTab: (tab: PatientStatusTab) => void;
};

/** Badge geometry, minus the sizing the Button primitive would otherwise impose. */
const PILL_CLASSNAME = 'h-auto gap-1.5 px-2.5 py-1 shadow-none [&_svg]:size-3.5';

export default function PatientStatusStrip({
    surveys,
    tests,
    reports,
    anamnesis,
    activeImports,
    onSelectTab,
}: Props) {
    const { items, lastActivity } = derivePatientStatus({
        surveys,
        tests,
        reports,
        anamnesis,
        activeImports,
    });

    return (
        <div className="mb-6 flex flex-wrap items-center gap-x-3 gap-y-2">
            {items.length === 0 ? (
                <Badge variant="success" className="gap-1.5 px-2.5 py-1 [&_svg]:size-3.5">
                    <TickCircle variant="Bulk" />
                    Tudo em dia
                </Badge>
            ) : (
                items.map((item) => {
                    const Icon = item.icon;

                    return (
                        <Button
                            key={item.key}
                            size="sm"
                            onClick={() => onSelectTab(item.tab)}
                            className={cn(
                                badgeVariants({ variant: item.tone }),
                                PILL_CLASSNAME,
                                'transition-opacity hover:opacity-80',
                            )}
                        >
                            <Icon variant="Bulk" />
                            {item.label}
                        </Button>
                    );
                })
            )}

            <span className="text-xs text-muted-foreground">
                {lastActivity
                    ? `Última atividade ${formatRelativeToNow(lastActivity)}`
                    : 'Nenhuma atividade registrada'}
            </span>
        </div>
    );
}
